import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, Package, CheckCircle, Clock, Download } from 'lucide-react';
import apiClient from '../../utils/api';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b', CONFIRMED: '#6366f1', DEPARTED: '#0ea5e9',
  IN_TRANSIT: '#3b82f6', DELIVERED: '#22c55e', CANCELLED: '#ef4444', RETURNED: '#94a3b8',
};

export const AgentReports: React.FC = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await apiClient.getReportStats(params);
      setStats(res.data.data);
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filters: any = {};
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.exportShipmentsCsv(filters);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `agent-report-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast('Report exported', 'success');
    } catch { toast('Export failed', 'error'); }
    finally { setExporting(false); }
  };

  const shipmentStats: any[] = stats?.shipmentStats ?? [];
  const dailyTrend: any[] = stats?.dailyTrend ?? [];
  const topRoutes: any[] = stats?.topRoutes ?? [];

  const totalShipments = shipmentStats.reduce((s: number, r: any) => s + parseInt(r.count), 0);
  const delivered = shipmentStats.find((r: any) => r.status === 'DELIVERED');
  const deliveredCount = delivered ? parseInt(delivered.count) : 0;
  const inTransit = shipmentStats.filter((r: any) => ['DEPARTED', 'IN_TRANSIT'].includes(r.status))
    .reduce((s: number, r: any) => s + parseInt(r.count), 0);
  const pending = shipmentStats.find((r: any) => r.status === 'PENDING');
  const pendingCount = pending ? parseInt(pending.count) : 0;
  const deliveryRate = totalShipments > 0 ? Math.round((deliveredCount / totalShipments) * 100) : 0;

  // Last 30 days trend
  const maxDaily = Math.max(...dailyTrend.map((d: any) => parseInt(d.count)), 1);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" style={{ width: 145 }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" style={{ width: 145 }} />
          <button className="btn btn-ghost" onClick={exportCsv} disabled={exporting}>
            <Download size={15} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {loading ? <div className="loading-spinner">Loading reports...</div> : (
        <>
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            {[
              { label: 'Total Shipments', value: totalShipments, icon: <Package size={20} />, color: '#3b82f6' },
              { label: 'Delivery Rate', value: `${deliveryRate}%`, icon: <TrendingUp size={20} />, color: '#22c55e' },
              { label: 'Delivered', value: deliveredCount, icon: <CheckCircle size={20} />, color: '#22c55e' },
              { label: 'In Transit', value: inTransit, icon: <Clock size={20} />, color: '#0ea5e9' },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{ borderLeft: `4px solid ${s.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value" style={{ color: s.color, fontSize: typeof s.value === 'string' ? 22 : 28 }}>{s.value}</div>
                  </div>
                  <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Shipments by Status</h2>
              {shipmentStats.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 14 }}>No data yet.</div>
              ) : shipmentStats.sort((a: any, b: any) => b.count - a.count).map((r: any) => (
                <div key={r.status} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                    <span style={{ fontWeight: 500 }}>{r.status.replace(/_/g, ' ')}</span>
                    <span style={{ color: '#64748b' }}>{r.count} ({totalShipments > 0 ? Math.round((r.count / totalShipments) * 100) : 0}%)</span>
                  </div>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${totalShipments > 0 ? (r.count / totalShipments) * 100 : 0}%`, background: STATUS_COLORS[r.status] || '#94a3b8', borderRadius: 4, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="card">
              <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Daily Shipments (Last 30 Days)</h2>
              {dailyTrend.length === 0 ? (
                <div style={{ color: '#94a3b8', fontSize: 14 }}>No data yet.</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, overflowX: 'auto' }}>
                  {dailyTrend.map((d: any) => (
                    <div key={d.date} title={`${d.date}: ${d.count}`} style={{ flex: '0 0 auto', width: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <div style={{ width: '100%', height: `${(parseInt(d.count) / maxDaily) * 100}px`, minHeight: parseInt(d.count) > 0 ? 3 : 0, background: 'linear-gradient(180deg, #3b82f6, #6366f1)', borderRadius: '3px 3px 0 0' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Top Destinations</h2>
            {topRoutes.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>No data yet.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {topRoutes.map((r: any, i: number) => (
                  <div key={r.receiverCity || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{r.receiverCity || 'Unknown'}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{r.count} shipments</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
